import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  TextInput,
  Divider,
  Card,
  Badge,
} from "@mantine/core";
import { notifySuccess } from "../../utils/notify";
import {
  IconGift,
  IconAlertCircle,
  IconSearch,
  IconLink,
} from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import { giftCardStatusColor } from "../../utils/giftCardStatus";
import { GiftCardSummary } from "../../types/giftCards";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";

interface AttachGiftCardModalProps {
  opened: boolean;
  onClose: () => void;
  salonId: string;
  clientId: string;
  /** Refetch the client's gift cards after a successful attach. */
  onSuccess: () => void | Promise<void>;
}

function _formatExpiry(expiresAt: string | null): string {
  if (!expiresAt) return "No expiry";
  const parsed = Date.parse(expiresAt);
  if (Number.isNaN(parsed)) return "No expiry";
  return `Expires ${new Date(parsed).toLocaleDateString()}`;
}

/**
 * Attach an already-purchased gift card to THIS client by its coupon code. Staff
 * enter the code, look it up (scoped to the salon), confirm the card, then
 * attach it. A card that already has a holder can't be reassigned — the lookup
 * surfaces "already attached" and the backend blocks the attach (409) so a
 * balance never moves off its rightful holder.
 */
export default function AttachGiftCardModal({
  opened,
  onClose,
  salonId,
  clientId,
  onSuccess,
}: AttachGiftCardModalProps) {
  const { isMobile } = useResponsive();
  const { lookupGiftCardByCode, attachGiftCard } = useApp();

  const [code, setCode] = useState("");
  const [card, setCard] = useState<GiftCardSummary | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isAttaching, setIsAttaching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setCode("");
      setCard(null);
      setIsLookingUp(false);
      setIsAttaching(false);
      setError(null);
    }
  }, [opened]);

  const trimmedCode = code.trim();

  const handleLookup = async () => {
    if (!trimmedCode || isLookingUp) return;
    setIsLookingUp(true);
    setError(null);
    setCard(null);
    try {
      const found = await lookupGiftCardByCode(salonId, trimmedCode);
      setCard(found);
    } catch (err) {
      console.error("Error looking up gift card:", err);
      setError(
        err instanceof Error
          ? err.message
          : "No gift card found with that code at this location.",
      );
    } finally {
      setIsLookingUp(false);
    }
  };

  // A card can be attached only when it has no holder yet and isn't terminal.
  const isAlreadyAttached = !!card?.is_attached;
  const isTerminal = card?.status === "voided" || card?.status === "expired";
  const canAttach = !!card && !isAlreadyAttached && !isTerminal && !isAttaching;

  const handleAttach = async () => {
    if (!card || !canAttach) return;
    setIsAttaching(true);
    setError(null);
    try {
      await attachGiftCard(salonId, card.id, { client_id: clientId });
      notifySuccess("The gift card is now on this client's profile.", {
        title: "Gift card attached",
      });
      try {
        await Promise.resolve(onSuccess());
      } catch (e) {
        console.error("Error in attach-gift-card success handler:", e);
      }
      onClose();
    } catch (err) {
      console.error("Error attaching gift card:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to attach the gift card. Please try again.",
      );
    } finally {
      setIsAttaching(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Attach Existing Gift Card</Text>}
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isAttaching}
      closeOnEscape={!isAttaching}
    >
      <BrandedLoadingOverlay visible={isAttaching} />

      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <Text size="sm" c="dimmed">
          Enter the gift card's code to attach it to this client. The card must
          not already belong to another client.
        </Text>

        <Group align="flex-end" gap="sm" wrap="nowrap">
          <TextInput
            label="Gift card code"
            placeholder="e.g. ABCD2345"
            value={code}
            onChange={(e) => {
              // Editing the code invalidates any prior lookup — clear the
              // confirmed card + error so a stale result can't be attached.
              setCode(e.currentTarget.value);
              setCard(null);
              setError(null);
            }}
            disabled={isLookingUp || isAttaching}
            style={{ flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleLookup();
            }}
          />
          <Button
            variant="light"
            color="var(--brand-teal)"
            leftSection={<IconSearch size={16} />}
            onClick={handleLookup}
            loading={isLookingUp}
            disabled={!trimmedCode || isAttaching}
          >
            Look up
          </Button>
        </Group>

        {card && (
          <Card withBorder padding="sm" radius="md">
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <IconGift size={20} />
                <Stack gap={0}>
                  <Text fw={600}>Code {card.code}</Text>
                  <Text size="sm" c="dimmed">
                    {formatCurrency(card.balance)} balance ·{" "}
                    {_formatExpiry(card.expires_at)}
                  </Text>
                </Stack>
              </Group>
              <Badge
                color={giftCardStatusColor(card.status)}
                variant="light"
                tt="capitalize"
              >
                {card.status}
              </Badge>
            </Group>

            {isAlreadyAttached && (
              <Alert
                mt="sm"
                icon={<IconAlertCircle size={16} />}
                color="yellow"
                variant="light"
              >
                This gift card is already attached to a client and can't be
                reassigned.
              </Alert>
            )}
            {!isAlreadyAttached && isTerminal && (
              <Alert
                mt="sm"
                icon={<IconAlertCircle size={16} />}
                color="yellow"
                variant="light"
              >
                A {card.status} gift card can't be attached.
              </Alert>
            )}
          </Card>
        )}

        <Divider />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isAttaching}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleAttach}
            loading={isAttaching}
            disabled={!canAttach}
            leftSection={<IconLink size={18} />}
          >
            Attach to this client
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
